// ===========================================
// Booking API Functions
// ===========================================
// API calls for makeup bookings and extra lesson requests

import { apiClient } from '../services/api';

// ===========================================
// MAKEUP BOOKING TYPES
// ===========================================

export interface MakeupCredit {
  studentId: string;
  studentName: string;
  termId: string;
  termName: string;
  creditsGranted: number;
  creditsUsed: number;
  creditsRedeemed: number;
  creditsRemaining: number;
}

export interface MakeupCreditLog {
  id: string;
  schoolId: string;
  studentId: string;
  termId: string;
  makeupCreditId: string;
  creditsAdded: number;
  reason: string;
  grantedByUserId: string;
  createdAt: string;
  GrantedBy: {
    firstName: string;
    lastName: string;
  };
  Term: {
    name: string;
  };
}

export interface MakeupEligibility {
  id: string;
  schoolId: string;
  studentId: string;
  attendanceId: string | null;
  lessonId: string | null;
  instrumentId: string | null;
  adminOffered: boolean;
  grantedAt: string;
  redeemedAt: string | null;
  redeemedSlotId: string | null;
  Student: {
    id: string;
    firstName: string;
    lastName: string;
  };
  Lesson: {
    id: string;
    name: string;
    dayOfWeek: number;
    startTime: string;
    Instructor?: {
      id: string;
      User: {
        firstName: string;
        lastName: string;
      };
    };
  } | null;
  Instrument: {
    id: string;
    name: string;
    color: string | null;
  } | null;
  Attendance: {
    id: string;
    date: string;
    status: string;
    absenceReason: string | null;
  } | null;
  /** The slot this eligibility was redeemed on (`eligibilityInclude` sends it without `Student`). */
  MakeupSlot: Omit<MakeupSlot, 'Student'> | null;
  /**
   * The term this offer's credit belongs to and the window it is judged by (#462). The SERVER
   * decides all of it (`offerCreditTerm.ts`); the page shows it and never recomputes it.
   * `creditTerm` is null only for an offer that cannot be placed in a term.
   */
  termId: string | null;
  creditTerm: { id: string; name: string; startDate: string; endDate: string } | null;
  /** The last day the credit may be used (a `@db.Date`), or null when no later term closes it. */
  validUntil: string | null;
  /** The window has closed: shown, never bookable. */
  expired: boolean;
}

export interface MakeupSlot {
  id: string;
  schoolId: string;
  termId: string;
  teacherId: string;
  roomId: string | null;
  instrumentId: string;
  date: string;
  startTime: string;
  endTime: string;
  isBooked: boolean;
  bookedByStudentId: string | null;
  bookedAt: string | null;
  // Raw Prisma relations, PascalCase — no camelCase aliases (#203: the parent
  // slot picker and bookings list read `slot.teacher`/`slot.room`, which never
  // arrive, and showed no teacher or room). Two sources share this shape:
  //  - booking history / DB slots (`slotInclude`): Room and Student may be set;
  //  - AVAILABLE slots are virtual (`getAvailableSlots` literal): Room and
  //    Student are always null.
  Teacher: {
    id: string;
    User: {
      firstName: string;
      lastName: string;
    };
  };
  Room: {
    id: string;
    name: string;
    Location: {
      name: string;
    };
  } | null;
  Instrument: {
    id: string;
    name: string;
    color: string | null;
  };
  Student: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface BookMakeupSlotInput {
  slotId: string;
  studentId: string;
  eligibilityId: string;
  // Optional fields for virtual slots (not yet in database)
  teacherId?: string;
  termId?: string;
  instrumentId?: string;
  roomId?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
}

export interface CreateMakeupSlotInput {
  termId: string;
  teacherId: string;
  roomId?: string;
  instrumentId: string;
  date: string;
  startTime: string;
  endTime: string;
}

// ===========================================
// EXTRA LESSON REQUEST TYPES
// ===========================================

export type ExtraLessonRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'SCHEDULED'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED';

export interface ExtraLessonRequest {
  id: string;
  schoolId: string;
  studentId: string;
  instrumentId: string | null;
  lessonTypeId: string | null;
  reason: string;
  notes: string | null;
  status: ExtraLessonRequestStatus;
  adminNotes: string | null;
  approvedAt: string | null;
  approvedById: string | null;
  rejectedAt: string | null;
  rejectedById: string | null;
  scheduledDate: string | null;
  scheduledTime: string | null;
  duration: number | null;
  teacherId: string | null;
  roomId: string | null;
  invoiceId: string | null;
  makeupCreditId?: string | null;
  /** The class a make-up replaces (holiday make-ups, 2026-09-28); null for an ordinary extra. */
  makeupForLessonId?: string | null;
  /**
   * Staff-only note (admin + teacher). On a PARENT response the server sends it
   * only when `shareNoteWithFamily` — otherwise it is null.
   */
  staffNote?: string | null;
  shareNoteWithFamily?: boolean;
  makeupForLesson?: { id: string; name: string; term: { id: string; name: string } } | null;
  createdAt: string;
  updatedAt: string;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    familyId: string | null;
  };
  instrument: {
    id: string;
    name: string;
    color: string | null;
  } | null;
  lessonType: {
    id: string;
    name: string;
  } | null;
  teacher: {
    id: string;
    user: {
      firstName: string;
      lastName: string;
    };
  } | null;
  room: {
    id: string;
    name: string;
    location: {
      name: string;
    };
  } | null;
  approvedBy: {
    firstName: string;
    lastName: string;
  } | null;
  rejectedBy: {
    firstName: string;
    lastName: string;
  } | null;
}

export interface CreateExtraLessonRequestInput {
  studentId: string;
  instrumentId?: string;
  lessonTypeId?: string;
  reason: string;
  notes?: string;
}

export interface AdminBookOnBehalfInput {
  studentId: string;
  instrumentId?: string;
  lessonTypeId?: string;
  reason: string;
  notes?: string;
  scheduledDate: string;
  scheduledTime: string;
  duration: number;
  teacherId: string;
  roomId?: string;
  // When true, deducts one make-up credit — from the TERM of `makeupForLessonId`,
  // which is what lets a make-up be booked in the school holidays.
  useMakeupCredit?: boolean;
  /** The class the make-up replaces. Required with `useMakeupCredit`. */
  makeupForLessonId?: string;
  /** Staff-only; REQUIRED by the server when the duration is not the class length. */
  staffNote?: string;
  shareNoteWithFamily?: boolean;
}

/**
 * A class an admin can book a make-up FOR (holiday make-ups, 2026-09-28): the
 * student's active enrolment, its term's credit balance, the class's default
 * make-up length and the last day the credit may be used (the day before the next
 * term starts; null = open-ended). ⚠️ Two classes in one term share ONE balance.
 */
export interface StudentMakeupOption {
  lessonId: string;
  lessonName: string;
  lessonType: string;
  instrumentName: string | null;
  teacherId: string;
  defaultDurationMins: number;
  termId: string;
  termName: string;
  termStartDate: string; // YYYY-MM-DD
  termEndDate: string; // YYYY-MM-DD
  creditValidUntil: string | null; // YYYY-MM-DD
  creditsGranted: number;
  creditsUsed: number;
  creditsRemaining: number;
}

export interface ScheduleExtraLessonInput {
  scheduledDate: string;
  scheduledTime: string;
  duration: number;
  teacherId: string;
  roomId?: string;
}

export interface RescheduleExtraLessonInput {
  scheduledDate: string;
  scheduledTime: string;
  duration?: number;
}

// Room-only move for a booked makeup slot or scheduled extra lesson.
export interface ChangeBookingSlotRoomInput {
  roomId: string;
  overrideConflicts?: boolean;
  reason?: string;
  notifyParents?: boolean;
}

export interface CreateDirectBookingInput {
  studentId: string;
  teacherId: string;
  instrumentId: string;
  lessonTypeId: string;
  scheduledDate: Date | string;
  startTime: string;
  duration: number;
  notes?: string;
}

export interface AvailableSlot {
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  reason?: 'BOOKED' | 'OVERLAPS';
}

// ===========================================
// RESPONSE WRAPPER TYPE
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// MAKEUP BOOKING API FUNCTIONS
// ===========================================

export const makeupBookingApi = {
  // Parent endpoints
  getInstruments: async (): Promise<{ id: string; name: string; color: string | null }[]> => {
    const response = await apiClient.get<ApiResponse<{ id: string; name: string; color: string | null }[]>>(
      '/bookings/makeup/instruments'
    );
    return response.data;
  },

  getEligibilities: async (): Promise<MakeupEligibility[]> => {
    const response = await apiClient.get<ApiResponse<MakeupEligibility[]>>(
      '/bookings/makeup/eligibility'
    );
    return response.data;
  },

  getCredits: async (): Promise<MakeupCredit[]> => {
    const response = await apiClient.get<ApiResponse<MakeupCredit[]>>(
      '/bookings/makeup/credits'
    );
    return response.data;
  },

  getAvailableSlots: async (
    instrumentId: string,
    termId?: string,
    teacherId?: string,
    eligibilityId?: string
  ): Promise<MakeupSlot[]> => {
    const params = new URLSearchParams({ instrumentId });
    if (termId) params.append('termId', termId);
    if (teacherId) params.append('teacherId', teacherId);
    // With an offer, the SERVER offers only the slots its credit window allows (#462).
    if (eligibilityId) params.append('eligibilityId', eligibilityId);
    const response = await apiClient.get<ApiResponse<MakeupSlot[]>>(
      `/bookings/makeup/slots?${params.toString()}`
    );
    return response.data;
  },

  bookSlot: async (input: BookMakeupSlotInput): Promise<MakeupSlot> => {
    const response = await apiClient.post<ApiResponse<MakeupSlot>>(
      '/bookings/makeup',
      input
    );
    return response.data;
  },

  cancelBooking: async (slotId: string): Promise<MakeupSlot> => {
    const response = await apiClient.delete<ApiResponse<MakeupSlot>>(
      `/bookings/makeup/${slotId}`
    );
    return response.data;
  },

  getHistory: async (): Promise<MakeupSlot[]> => {
    const response = await apiClient.get<ApiResponse<MakeupSlot[]>>(
      '/bookings/makeup/history'
    );
    return response.data;
  },

  // Admin endpoints
  adminGetCredits: async (termId?: string): Promise<MakeupCredit[]> => {
    const params = termId ? `?termId=${termId}` : '';
    const response = await apiClient.get<ApiResponse<MakeupCredit[]>>(
      `/bookings/makeup/admin/credits${params}`
    );
    return response.data;
  },

  adminGetStudentCredits: async (studentId: string, termId: string): Promise<MakeupCredit> => {
    const response = await apiClient.get<ApiResponse<MakeupCredit>>(
      `/bookings/makeup/admin/credits/${studentId}/${termId}`
    );
    return response.data;
  },

  /**
   * Add make-up credit (#574). `offerToFamily` is the choice between the two actions: true = "Send
   * make-up credit" (the family also gets a Book button), false = "Add more make-up credit" (the
   * balance only, for the office to use). The URL keeps its old name; only the words changed.
   */
  adminGrantCredit: async (
    studentId: string,
    termId: string,
    additionalCredits?: number,
    reason?: string,
    offerToFamily?: boolean
  ): Promise<MakeupCredit> => {
    const response = await apiClient.post<ApiResponse<MakeupCredit>>(
      `/bookings/makeup/admin/credits/${studentId}/${termId}/grant`,
      { additionalCredits, reason: reason || 'Added by admin', offerToFamily }
    );
    return response.data;
  },

  adminGetStudentCreditLogs: async (studentId: string): Promise<MakeupCreditLog[]> => {
    const response = await apiClient.get<ApiResponse<MakeupCreditLog[]>>(
      `/bookings/makeup/admin/credits/${studentId}/logs`
    );
    return response.data;
  },

  adminAdjustCredits: async (
    creditId: string,
    creditsGranted: number,
    creditsUsed: number
  ): Promise<MakeupCredit> => {
    const response = await apiClient.put<ApiResponse<MakeupCredit>>(
      `/bookings/makeup/admin/credits/${creditId}`,
      { creditsGranted, creditsUsed }
    );
    return response.data;
  },

  adminGetSlots: async (params?: {
    termId?: string;
    teacherId?: string;
    includeBooked?: boolean;
  }): Promise<MakeupSlot[]> => {
    const searchParams = new URLSearchParams();
    if (params?.termId) searchParams.append('termId', params.termId);
    if (params?.teacherId) searchParams.append('teacherId', params.teacherId);
    if (params?.includeBooked !== undefined) {
      searchParams.append('includeBooked', String(params.includeBooked));
    }
    const queryString = searchParams.toString();
    const response = await apiClient.get<ApiResponse<MakeupSlot[]>>(
      `/bookings/makeup/admin/slots${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },

  adminCreateSlot: async (input: CreateMakeupSlotInput): Promise<MakeupSlot> => {
    const response = await apiClient.post<ApiResponse<MakeupSlot>>(
      '/bookings/makeup/admin/slots',
      input
    );
    return response.data;
  },

  adminRescheduleSlot: async (
    slotId: string,
    input: { date: string; startTime: string; endTime: string; roomId?: string | null }
  ): Promise<MakeupSlot> => {
    const response = await apiClient.patch<ApiResponse<MakeupSlot>>(
      `/bookings/makeup/admin/slots/${slotId}/reschedule`,
      input
    );
    return response.data;
  },

  // Change the room/location of a booked makeup slot (room-only move).
  adminChangeSlotRoom: async (
    slotId: string,
    input: ChangeBookingSlotRoomInput
  ): Promise<MakeupSlot> => {
    const response = await apiClient.patch<ApiResponse<MakeupSlot>>(
      `/bookings/makeup/admin/slots/${slotId}/room`,
      input
    );
    return response.data;
  },

  adminDeleteSlot: async (slotId: string): Promise<void> => {
    await apiClient.delete(`/bookings/makeup/admin/slots/${slotId}`);
  },

  adminGetEligibilities: async (params?: {
    studentId?: string;
    unredeemedOnly?: boolean;
  }): Promise<MakeupEligibility[]> => {
    const searchParams = new URLSearchParams();
    if (params?.studentId) searchParams.append('studentId', params.studentId);
    if (params?.unredeemedOnly !== undefined) {
      searchParams.append('unredeemedOnly', String(params.unredeemedOnly));
    }
    const queryString = searchParams.toString();
    const response = await apiClient.get<ApiResponse<MakeupEligibility[]>>(
      `/bookings/makeup/admin/eligibilities${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },
};

// ===========================================
// EXTRA LESSON REQUEST API FUNCTIONS
// ===========================================

/**
 * Transform backend response (PascalCase relations) to frontend format (camelCase)
 */
function transformExtraLessonRequest(req: any): ExtraLessonRequest {
  return {
    ...req,
    student: req.Student,
    instrument: req.Instrument,
    lessonType: req.LessonType,
    teacher: req.Teacher ? {
      id: req.Teacher.id,
      user: {
        firstName: req.Teacher.User.firstName,
        lastName: req.Teacher.User.lastName,
      },
    } : null,
    room: req.Room ? {
      id: req.Room.id,
      name: req.Room.name,
      location: {
        name: req.Room.Location.name,
      },
    } : null,
    approvedBy: req.User_ExtraLessonRequest_approvedByIdToUser,
    rejectedBy: req.User_ExtraLessonRequest_rejectedByIdToUser,
    makeupForLesson: req.MakeupForLesson
      ? {
          id: req.MakeupForLesson.id,
          name: req.MakeupForLesson.name,
          term: { id: req.MakeupForLesson.Term.id, name: req.MakeupForLesson.Term.name },
        }
      : null,
  };
}

export const extraLessonApi = {
  // Parent endpoints
  getRequests: async (): Promise<ExtraLessonRequest[]> => {
    const response = await apiClient.get<ApiResponse<any[]>>(
      '/bookings/extra'
    );
    return response.data.map(transformExtraLessonRequest);
  },

  createRequest: async (input: CreateExtraLessonRequestInput): Promise<ExtraLessonRequest> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/bookings/extra',
      input
    );
    return transformExtraLessonRequest(response.data);
  },

  cancelRequest: async (requestId: string): Promise<ExtraLessonRequest> => {
    const response = await apiClient.delete<ApiResponse<any>>(
      `/bookings/extra/${requestId}`
    );
    return transformExtraLessonRequest(response.data);
  },

  // Direct booking endpoints
  getTeacherAvailabilityDays: async (teacherId: string): Promise<number[]> => {
    const response = await apiClient.get<ApiResponse<number[]>>(
      `/bookings/extra/teacher-availability-days/${teacherId}`
    );
    return response.data;
  },

  getAvailableSlots: async (
    teacherId: string,
    date: Date | string,
    duration: number
  ): Promise<AvailableSlot[]> => {
    const dateStr =
      typeof date === 'string'
        ? date
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const response = await apiClient.get<ApiResponse<AvailableSlot[]>>(
      `/bookings/extra/available-slots/${teacherId}?date=${encodeURIComponent(dateStr)}&duration=${duration}`
    );
    return response.data;
  },

  createDirectBooking: async (input: CreateDirectBookingInput): Promise<ExtraLessonRequest> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/bookings/extra/direct',
      input
    );
    return transformExtraLessonRequest(response.data);
  },

  // Settings endpoint (for parent UI)
  getSettings: async () => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/bookings/extra/settings'
    );
    return response.data;
  },

  // Admin endpoints
  adminGetRequests: async (params?: {
    status?: ExtraLessonRequestStatus;
    studentId?: string;
  }): Promise<ExtraLessonRequest[]> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.studentId) searchParams.append('studentId', params.studentId);
    const queryString = searchParams.toString();
    const response = await apiClient.get<ApiResponse<any[]>>(
      `/bookings/extra/admin${queryString ? `?${queryString}` : ''}`
    );
    return response.data.map(transformExtraLessonRequest);
  },

  adminGetPendingCount: async (): Promise<number> => {
    const response = await apiClient.get<ApiResponse<{ count: number }>>(
      '/bookings/extra/admin/pending-count'
    );
    return response.data.count;
  },

  adminGetRequest: async (requestId: string): Promise<ExtraLessonRequest> => {
    const response = await apiClient.get<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}`
    );
    return transformExtraLessonRequest(response.data);
  },

  adminApprove: async (requestId: string, adminNotes?: string): Promise<ExtraLessonRequest> => {
    const response = await apiClient.put<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}/approve`,
      { adminNotes }
    );
    return transformExtraLessonRequest(response.data);
  },

  adminReject: async (requestId: string, adminNotes: string): Promise<ExtraLessonRequest> => {
    const response = await apiClient.put<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}/reject`,
      { adminNotes }
    );
    return transformExtraLessonRequest(response.data);
  },

  adminSchedule: async (
    requestId: string,
    input: ScheduleExtraLessonInput
  ): Promise<ExtraLessonRequest> => {
    const response = await apiClient.put<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}/schedule`,
      input
    );
    return transformExtraLessonRequest(response.data);
  },

  adminReschedule: async (
    requestId: string,
    input: RescheduleExtraLessonInput
  ): Promise<ExtraLessonRequest> => {
    const response = await apiClient.patch<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}/reschedule`,
      input
    );
    return transformExtraLessonRequest(response.data);
  },

  // Change the room/location of a scheduled extra lesson (room-only move).
  adminChangeRoom: async (
    requestId: string,
    input: ChangeBookingSlotRoomInput
  ): Promise<ExtraLessonRequest> => {
    const response = await apiClient.patch<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}/room`,
      input
    );
    return transformExtraLessonRequest(response.data);
  },

  adminComplete: async (requestId: string): Promise<ExtraLessonRequest> => {
    const response = await apiClient.put<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}/complete`,
      {}
    );
    return transformExtraLessonRequest(response.data);
  },

  adminLinkInvoice: async (requestId: string, invoiceId: string): Promise<ExtraLessonRequest> => {
    const response = await apiClient.put<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}/invoice`,
      { invoiceId }
    );
    return transformExtraLessonRequest(response.data);
  },

  adminGetTeacherAvailabilityDays: async (teacherId: string): Promise<number[]> => {
    const response = await apiClient.get<ApiResponse<number[]>>(
      `/bookings/extra/admin/teacher-availability-days/${teacherId}`
    );
    return response.data;
  },

  adminGetAvailableSlots: async (
    teacherId: string,
    date: Date | string,
    duration: number
  ): Promise<AvailableSlot[]> => {
    const dateStr =
      typeof date === 'string'
        ? date
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const response = await apiClient.get<ApiResponse<AvailableSlot[]>>(
      `/bookings/extra/admin/available-slots/${teacherId}?date=${encodeURIComponent(dateStr)}&duration=${duration}`
    );
    return response.data;
  },

  adminBookOnBehalf: async (input: AdminBookOnBehalfInput): Promise<ExtraLessonRequest> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/bookings/extra/admin/book',
      input
    );
    return transformExtraLessonRequest(response.data);
  },

  adminGetStudentMakeupOptions: async (studentId: string): Promise<StudentMakeupOption[]> => {
    const response = await apiClient.get<ApiResponse<StudentMakeupOption[]>>(
      `/bookings/extra/admin/student-makeup-options/${studentId}`
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  adminCancel: async (
    requestId: string,
    reason?: string,
    // Omitted means SEND — the backend schema defaults it to true, so a caller
    // that doesn't pass it can never accidentally silence the family.
    notifyParents?: boolean
  ): Promise<ExtraLessonRequest> => {
    const response = await apiClient.delete<ApiResponse<any>>(
      `/bookings/extra/admin/${requestId}`,
      { data: { reason, notifyParents } }
    );
    return transformExtraLessonRequest(response.data);
  },
};
